/**
 * PDF Upload Interface - Component để upload và preview PDF
 */

import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, X, Loader2, AlertCircle, CheckCircle, Eye, Sparkles } from 'lucide-react'
import { pdfProcessingService } from '../../services/pdf/PDFProcessingService'
import { pdfVisionService } from '../../services/pdf/PDFVisionService'
import type { PDFProcessingResult } from '../../services/pdf/PDFProcessingService'

interface PDFUploadInterfaceProps {
  onPDFProcessed: (result: PDFProcessingResult & { file: File }) => void
  maxFileSizeMB?: number
}

const PDFUploadInterface: React.FC<PDFUploadInterfaceProps> = ({
  onPDFProcessed,
  maxFileSizeMB = 10
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingStatus, setProcessingStatus] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [processedResult, setProcessedResult] = useState<PDFProcessingResult | null>(null)
  const [showVisionOption, setShowVisionOption] = useState(false)
  const [isUsingVision, setIsUsingVision] = useState(false)
  const [visionProgress, setVisionProgress] = useState({ current: 0, total: 0 })
  const [concurrentPages, setConcurrentPages] = useState(3) // Default: 3 pages đồng thời

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setError('')
    setProcessedResult(null)

    if (acceptedFiles.length === 0) {
      setError('Vui lòng chọn file PDF hợp lệ')
      return
    }

    const file = acceptedFiles[0]

    // Validate file type
    if (!file.type.includes('pdf')) {
      setError('File phải là định dạng PDF')
      return
    }

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024)
    if (fileSizeMB > maxFileSizeMB) {
      setError(`File quá lớn (${fileSizeMB.toFixed(2)}MB). Tối đa ${maxFileSizeMB}MB`)
      return
    }

    setUploadedFile(file)
    await processPDF(file)
  }, [maxFileSizeMB])

  // Process PDF
  const processPDF = async (file: File) => {
    setIsProcessing(true)
    setProcessingStatus('Đang đọc file PDF...')
    setError('')

    try {
      // Extract text from PDF
      const result = await pdfProcessingService.extractTextFromPDF(file, {
        maxPages: 50,
        removeExtraWhitespace: true,
        normalizeText: true
      })

      if (!result.success) {
        setError(result.error || 'Có lỗi xảy ra khi xử lý PDF')
        setIsProcessing(false)
        
        // Show Vision option if it's a scan PDF
        if (result.error?.includes('scan') || result.error?.includes('text')) {
          setShowVisionOption(true)
        }
        return
      }

      setProcessingStatus('Đang trích xuất từ khóa...')

      // Extract keywords
      const keywords = pdfProcessingService.extractKeywords(result.text || '', 15)
      
      setProcessedResult(result)
      setProcessingStatus('Hoàn thành!')

      // Notify parent component
      onPDFProcessed({
        ...result,
        file
      })

      console.log('✅ PDF processed successfully')
      console.log('📊 Text length:', result.text?.length, 'characters')
      console.log('📄 Page count:', result.pageCount)
      console.log('🏷️ Keywords:', keywords)

    } catch (err) {
      console.error('Error processing PDF:', err)
      setError('Có lỗi xảy ra khi xử lý PDF. Vui lòng thử lại.')
    } finally {
      setIsProcessing(false)
    }
  }

  // Process with Vision AI (for scanned PDFs)
  const processWithVision = async () => {
    if (!uploadedFile) return

    setIsProcessing(true)
    setIsUsingVision(true)
    setError('')
    setShowVisionOption(false)
    
    // Get page count first
    const arrayBuffer = await uploadedFile.arrayBuffer()
    const loadingTask = await import('pdfjs-dist').then(pdfjsLib => pdfjsLib.getDocument({ data: arrayBuffer }))
    const pdf = await loadingTask.promise
    const totalPages = Math.min(pdf.numPages, 10)
    
    setVisionProgress({ current: 0, total: totalPages })
    setProcessingStatus(`Đang phân tích ${totalPages} trang với Vision AI (${concurrentPages} trang/lần)...`)

    try {
      const visionResult = await pdfVisionService.processPDFWithVision(uploadedFile, 10, concurrentPages)

      if (!visionResult.success) {
        setError(visionResult.error || 'Không thể xử lý với Vision AI')
        setIsProcessing(false)
        setIsUsingVision(false)
        return
      }

      setProcessingStatus('Đang trích xuất từ khóa...')

      // Extract keywords
      const keywords = pdfProcessingService.extractKeywords(visionResult.content || '', 15)
      
      const result: PDFProcessingResult = {
        success: true,
        text: visionResult.content,
        pageCount: visionResult.pageContents?.length,
        metadata: { title: uploadedFile.name }
      }

      setProcessedResult(result)
      setProcessingStatus('Hoàn thành với Vision AI!')

      // Notify parent component
      onPDFProcessed({
        ...result,
        file: uploadedFile
      })

      console.log('✅ PDF processed with Vision successfully')
      console.log('📊 Content length:', visionResult.content?.length, 'characters')
      console.log('🪙 Tokens used:', visionResult.tokensUsed)
      console.log('🏷️ Keywords:', keywords)

    } catch (err) {
      console.error('Error processing with Vision:', err)
      setError('Có lỗi xảy ra khi xử lý với Vision AI. Vui lòng thử lại.')
    } finally {
      setIsProcessing(false)
      setIsUsingVision(false)
    }
  }

  // Remove uploaded file
  const removeFile = () => {
    setUploadedFile(null)
    setProcessedResult(null)
    setError('')
    setProcessingStatus('')
    setShowVisionOption(false)
    setIsUsingVision(false)
  }

  // Setup dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    disabled: isProcessing
  })

  return (
    <div className="w-full">
      {/* Upload Area */}
      {!uploadedFile && (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
            transition-all duration-200
            ${isDragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
            }
            ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input {...getInputProps()} />
          
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
              <Upload className="w-8 h-8 text-blue-600" />
            </div>
            
            <div>
              <p className="text-lg font-semibold text-gray-700 mb-1">
                {isDragActive ? 'Thả file PDF vào đây' : 'Kéo thả file PDF hoặc click để chọn'}
              </p>
              <p className="text-sm text-gray-500">
                Hỗ trợ file PDF, tối đa {maxFileSizeMB}MB
              </p>
            </div>
          </div>
        </div>
      )}

      {/* File Info & Processing Status */}
      {uploadedFile && (
        <div className="border-2 border-blue-300 rounded-xl p-6 bg-blue-50">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-3 flex-1">
              <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 truncate">
                  {uploadedFile.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {(uploadedFile.size / 1024).toFixed(2)} KB
                </p>
                
                {processedResult && (
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-gray-600">
                      📄 Số trang: {processedResult.pageCount}
                    </p>
                    <p className="text-sm text-gray-600">
                      📝 Nội dung: {processedResult.text?.length.toLocaleString()} ký tự
                    </p>
                    {processedResult.metadata?.title && (
                      <p className="text-sm text-gray-600">
                        📌 Tiêu đề: {processedResult.metadata.title}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {!isProcessing && (
              <button
                onClick={removeFile}
                className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                title="Xóa file"
              >
                <X className="w-5 h-5 text-red-600" />
              </button>
            )}
          </div>

          {/* Processing Status */}
          {isProcessing && (
            <div className="space-y-3">
              <div className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="text-sm font-medium text-gray-700">
                  {processingStatus}
                </span>
              </div>
              
              {/* Vision Progress Bar */}
              {isUsingVision && visionProgress.total > 0 && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-purple-700">
                      Vision AI Progress
                    </span>
                    <span className="text-xs font-bold text-purple-900">
                      {Math.round((visionProgress.current / visionProgress.total) * 100)}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-purple-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                      style={{ width: `${(visionProgress.current / visionProgress.total) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-purple-600 mt-2">
                    Đang xử lý trang {visionProgress.current} / {visionProgress.total} ({concurrentPages} trang/lần)
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Success Status */}
          {processedResult && !isProcessing && (
            <div className="flex items-center space-x-3 p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-700">
                Đã xử lý PDF thành công! Sẵn sàng tạo Flashcards.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
          <div className="flex items-start space-x-3 mb-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-red-800 mb-1">Không thể xử lý PDF</h4>
              <p className="text-sm text-red-700 mb-3">{error}</p>
            </div>
          </div>
          
          {error.includes('scan') && (
            <div className="ml-9 space-y-3">
              {/* Vision AI Option */}
              {showVisionOption && (
                <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg">
                  <div className="flex items-start space-x-3 mb-3">
                    <Eye className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h5 className="text-sm font-bold text-purple-900 mb-1">
                        🚀 Thử GPT-4 Vision AI (Beta)
                      </h5>
                      <p className="text-sm text-purple-700 mb-2">
                        Công nghệ Vision AI có thể "nhìn" và hiểu PDF scan, bảng biểu, hình ảnh, công thức - giống NotebookLM!
                      </p>
                      <ul className="text-xs text-purple-600 space-y-1 mb-3">
                        <li>✓ Đọc được PDF scan (không cần OCR)</li>
                        <li>✓ Hiểu bảng biểu, số liệu</li>
                        <li>✓ Extract công thức Vật lí</li>
                        <li>✓ Mô tả hình vẽ, đồ thị</li>
                        <li>⚠️ Chi phí: ~$0.05/page</li>
                        <li>⚠️ Thời gian: ~5-7 giây/page (với {concurrentPages} trang/lần)</li>
                      </ul>
                      
                      {/* Speed Control */}
                      <div className="mb-4 p-3 bg-white rounded-lg border border-purple-200">
                        <label className="text-xs font-semibold text-purple-800 mb-2 block">
                          ⚡ Tốc độ xử lý: {concurrentPages} trang đồng thời
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="5"
                          value={concurrentPages}
                          onChange={(e) => setConcurrentPages(parseInt(e.target.value))}
                          className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                        />
                        <div className="flex justify-between text-xs text-purple-600 mt-1">
                          <span>Chậm (1)</span>
                          <span>Nhanh (5)</span>
                        </div>
                        <p className="text-xs text-purple-600 mt-2">
                          {concurrentPages === 1 && '🐢 An toàn nhất, tránh rate limit'}
                          {concurrentPages === 2 && '🚶 Cân bằng tốc độ và ổn định'}
                          {concurrentPages === 3 && '⚡ Khuyến nghị (tối ưu)'}
                          {concurrentPages === 4 && '🚀 Nhanh, có thể rate limit'}
                          {concurrentPages === 5 && '💨 Nhanh nhất, dễ lỗi rate limit'}
                        </p>
                      </div>
                      
                      <button
                        onClick={processWithVision}
                        disabled={isProcessing}
                        className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
                      >
                        <Sparkles className="w-5 h-5" />
                        <span>Xử lý với Vision AI ({concurrentPages} trang/lần)</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Regular tips */}
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h5 className="text-sm font-semibold text-yellow-800 mb-2">💡 Hoặc thử các cách khác:</h5>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Sử dụng PDF text-based (có thể copy được text)</li>
                  <li>• Nếu PDF là scan, hãy OCR trước khi upload</li>
                  <li>• Test bằng cách mở PDF và thử copy text</li>
                  <li>• Thử PDF mẫu khác (tài liệu Word xuất ra PDF thường OK)</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
          <FileText className="w-4 h-4 mr-2" />
          Hướng dẫn sử dụng
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>PDF text-based:</strong> Chọn file PDF có thể copy text (không phải scan)</li>
          <li>• <strong>Nội dung:</strong> Tài liệu học tập về Vật lí (tối thiểu 50 ký tự)</li>
          <li>• <strong>Kích thước:</strong> Tối đa 10MB, tối đa 50 trang</li>
          <li>• <strong>AI sẽ:</strong> Phân tích và tạo ~15 flashcards từ nội dung PDF</li>
          <li>• <strong>Bạn có thể:</strong> Chỉnh sửa flashcards trước khi lưu</li>
          <li>• <strong>Giới hạn:</strong> Chỉ tạo flashcards từ nội dung trong tài liệu</li>
        </ul>
        
        <div className="mt-3 p-2 bg-white rounded border border-blue-200">
          <p className="text-xs text-blue-700">
            <strong>Lưu ý:</strong> Nếu PDF không extract được text (0 ký tự), đó là PDF scan.
            Hãy sử dụng PDF được tạo từ Word/Google Docs hoặc PDF có text layer.
          </p>
        </div>
      </div>
    </div>
  )
}

export default PDFUploadInterface

